import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Error, Label, Switch } from "../..";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormModalSchema } from "../../../Schema/Configuration/FormSchema";
import { getAxios, postAxios, putAxios } from "../../../Services/commonService";
import { API_FORM, API_PERMISSION } from "../../../../utils/api/ApiConstant";
import TreeSelect from "../../UI/TreeSelect";
import { generatePath } from "../../../../utils/pathGenerator";
import { lowerFirstLetterUsingRegex } from "../../../helper/common";
import {
  sanitizeKeyInput,
  sanitizeNameInput,
} from "../../../../utils/inputUtils";

const FormModal = ({ handleModalHide, rowData, fetchData }) => {
  const isEdit = rowData?.id && Object.keys(rowData)?.length > 0;

  const [permissionList, setPermissionList] = useState([]);
  const [formData, setFormData] = useState(null);

  const filterOutNode = useCallback((tree, excludeId) => {
    if (!tree?.length) return [];

    return tree.reduce((acc, node) => {
      if (node.value === excludeId) return acc;
      const filteredChildren = node.children?.length
        ? filterOutNode(node.children, excludeId)
        : [];
      if (node.isPage === false) {
        acc.push({
          ...node,
          children: filteredChildren,
        });
      }

      return acc;
    }, []);
  }, []);

  const filteredParentIdList = useMemo(() => {
    return filterOutNode(rowData?.parentIdList || [], rowData?.id);
  }, [rowData?.parentIdList, rowData?.id, filterOutNode]);

  const findParentLabel = useCallback((parentId, tree) => {
    for (const node of tree) {
      if (node.value === parentId) {
        return node.label;
      }
      if (node.children && node.children.length > 0) {
        const found = findParentLabel(parentId, node.children);
        if (found) return found;
      }
    }
    return null;
  }, []);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name: null,
      key: null,
      parentId: null,
      path: null,
      displayIndex: null,
      description: null,
      permissions: null,
      icon: "",
      isPage: true,
    },
    resolver: yupResolver(FormModalSchema),
  });

  const watchName = watch("name");
  const watchParentId = watch("parentId");
  const watchIsPage = watch("isPage");

  useEffect(() => {
    if (!watchIsPage) {
      setValue("path", "");
      return;
    }
    const generatedPath = generatePath(
      watchParentId,
      rowData?.parentIdList,
      watchName,
    );
    setValue("path", generatedPath);
  }, [
    watchName,
    watchParentId,
    watchIsPage,
    rowData?.parentIdList,
    setValue,
    isEdit,
  ]);

  const fetchPermissionData = useCallback(async () => {
    const response = await getAxios(API_PERMISSION);
    const list = response.map((permission) => ({
      label: permission?.name,
      value: permission?.id,
    }));
    setPermissionList(list);
  }, []);

  const fetchFormDetail = useCallback(async () => {
    if (!isEdit) return;
    try {
      const response = await getAxios(`${API_FORM}/${rowData.id}`);
      setFormData(response);
    } catch (error) {
      console.error("Failed to fetch form detail:", error);
    }
  }, [isEdit, rowData?.id]);

  useEffect(() => {
    fetchFormDetail();
    fetchPermissionData();
  }, [fetchPermissionData, fetchFormDetail]);

  // useEffect(() => {
  //   if (permissionList.length === 0) return;
  //   const viewPermission = permissionList.find(
  //     (p) => p.label?.toLowerCase() === "view",
  //   );
  //   if (viewPermission) {
  //     setValue("permissions", [viewPermission]);
  //   }
  // }, [permissionList]);

  useEffect(() => {
    if (isEdit && formData && permissionList.length > 0) {
      const assignedPermissionIds =
        formData.permissions?.map((perm) => perm.permissionId) ?? [];

      const mappedPermissions = permissionList
        .filter((perm) => assignedPermissionIds.includes(perm.value))
        .map((perm) => {
          const original = formData.permissions.find(
            (p) => p.permissionId === perm.value,
          );
          return {
            label: perm.label,
            value: perm.value,
            id: original?.id,
            formId: original?.formId,
            isActive: original?.isActive,
            rowVersion: original?.rowVersion,
          };
        });

      reset({
        name: formData.name ?? null,
        key: formData.key ?? null,
        parentId: formData.parentId
          ? {
              label:
                findParentLabel(
                  formData.parentId,
                  rowData?.parentIdList || [],
                ) ?? String(formData.parentId),
              value: formData.parentId,
            }
          : null,
        path: formData.path ?? null,
        displayIndex: formData.displayIndex ?? null,
        description: formData.description ?? null,
        permissions: mappedPermissions,
        isPage: formData.isPage ?? true,
        icon: formData.icon ?? "",
      });
    }
  }, [
    isEdit,
    formData,
    permissionList,
    reset,
    findParentLabel,
    rowData?.parentIdList,
  ]);

  const handleFormSubmit = async (formValues) => {
    const payload = {
      ...formValues,
      parentId: formValues?.parentId ? formValues?.parentId?.value : null,
      permissions:
        formValues?.isPage && formValues?.permissions
          ? formValues.permissions.map((item) => item.value)
          : null,
    };
    const url = `${API_FORM}${isEdit ? "/" + rowData?.id : ""}`;
    const method = isEdit ? putAxios : postAxios;

    if (isEdit) {
      payload.isActive = formData?.isActive;
      payload.rowVersion = formData?.rowVersion;
    }

    const res = await method(url, payload);

    if (res?.success) {
      fetchData();
      handleModalHide();
      return;
    }

    res?.errors?.forEach((element) => {
      const field = lowerFirstLetterUsingRegex(element?.field);
      if (field)
        setError(field, {
          type: "manual",
          message: element.message,
        });
    });
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <fieldset disabled={isSubmitting}>
        <div className="row align-items-end">
          {/* Name */}
          <div className="col-lg-4 col-md-6 col-12 ">
            <div className="form-group">
              <Label htmlFor="name" className="form-label" isRequired={true}>
                Name
              </Label>
              <input
                className="form-control"
                type="text"
                id="name"
                placeholder="Enter name"
                autoFocus={true}
                tabIndex={1}
                {...register("name", {
                  onChange: (e) => {
                    e.target.value = sanitizeNameInput(e.target.value);
                  },
                })}
              />
              {errors.name && <Error>{errors.name.message}</Error>}
            </div>
          </div>

          {/* Key */}
          <div className="col-lg-4 col-md-6 col-12">
            <div className="form-group">
              <Label htmlFor="key" className="form-label" isRequired={true}>
                Key
              </Label>
              <input
                className="form-control"
                type="text"
                id="key"
                placeholder="Enter key"
                tabIndex={2}
                {...register("key", {
                  onChange: (e) => {
                    e.target.value = sanitizeKeyInput(e.target.value);
                  },
                })}
              />
              {errors.key && <Error>{errors.key.message}</Error>}
            </div>
          </div>

          {/* icon */}
          <div className="col-lg-4 col-md-6 col-12">
            <div className="form-group">
              <Label htmlFor="icon" className="form-label">
                Icon
              </Label>
              <input
                className="form-control"
                type="text"
                id="icon"
                placeholder="Enter icon"
                {...register("icon", {
                  onChange: (e) => {
                    e.target.value = sanitizeKeyInput(e.target.value);
                  },
                })}
                tabIndex={3}
              />
            </div>
          </div>

          {/* Parent Form */}
          {watchIsPage && (
            <div className="col-lg-4 col-md-6 col-12">
              <div className="form-group">
                <Label
                  htmlFor="parentId"
                  className="form-label"
                  isRequired={true}
                >
                  Parent Form
                </Label>
                <Controller
                  name="parentId"
                  control={control}
                  render={({ field }) => (
                    <TreeSelect
                      options={filteredParentIdList}
                      value={field.value}
                      onChange={(val) => field.onChange(val)}
                      isClearable={true}
                      placeholder="Select parent..."
                      error={errors.parentId}
                      tabIndex={4}
                    />
                  )}
                />
                {errors.parentId && <Error>{errors.parentId.message}</Error>}
              </div>
            </div>
          )}

          {/* Path */}
          {watchIsPage && (
            <div className="col-lg-4 col-md-6 col-12">
              <div className="form-group">
                <Label htmlFor="path" className="form-label" isRequired={true}>
                  Path
                </Label>
                <input
                  className="form-control"
                  type="text"
                  id="path"
                  placeholder="Enter path"
                  {...register("path")}
                  disabled={true}
                />
                {errors.path && <Error>{errors.path.message}</Error>}
              </div>
            </div>
          )}

          {/* Display Index */}
          <div className="col-lg-4 col-md-6 col-12">
            <div className="form-group">
              <Label
                htmlFor="displayIndex"
                className="form-label"
                isRequired={true}
              >
                Display Index
              </Label>
              <input
                className="form-control"
                type="number"
                id="displayIndex"
                placeholder="Enter Display Index"
                {...register("displayIndex")}
                tabIndex={5}
              />
              {errors.displayIndex && (
                <Error>{errors.displayIndex.message}</Error>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="col-lg-8 col-md-6 col-12">
            <div className="form-group">
              <Label htmlFor="description" className="form-label">
                Description
              </Label>
              <input
                className="form-control"
                type="text"
                id="description"
                placeholder="Enter description"
                {...register("description")}
                tabIndex={6}
              />
              {errors.description && (
                <Error>{errors.description.message}</Error>
              )}
            </div>
          </div>

          <div className="col-lg-4 col-md-6 col-12">
            <div className="form-group w-100 ">
              <Label htmlFor="isPage" className="form-label">
                Is Page
              </Label>

              <div className="form-control d-flex justify-content-start gap-2">
                <Controller
                  name="isPage"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      name="isPage"
                      className="m-0"
                      onChange={(val) => {
                        field.onChange(val);
                        if (!val) {
                          setValue("permissions", null);
                        }
                      }}
                      getValue={() => field.value}
                      value={field.value}
                      tabIndex={7}
                    />
                  )}
                />
                <span>Yes / No</span>
              </div>
            </div>
          </div>

          {watchIsPage && (
            <div className="col-12">
              <div className="input_control">
                <Label className="form-label">Permissions</Label>
                <Controller
                  name="permissions"
                  control={control}
                  render={({ field }) => (
                    <div className="d-flex flex-row flex-wrap-wrap flex-wrap gap-3 my-2">
                      {permissionList.map((permission, index) => {
                        const isView =
                          permission.label?.toLowerCase() === "view";
                        const isChecked =
                          field.value?.some(
                            (item) => item.value === permission.value,
                          ) ?? false;

                        return (
                          <div className="form-check  " key={permission.value}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`permission-${permission.value}`}
                              checked={isChecked}
                              tabIndex={8 + index}
                              // disabled={isView}
                              onChange={(e) => {
                                const viewPermission = permissionList.find(
                                  (p) => p.label?.toLowerCase() === "view",
                                );
                                if (e.target.checked) {
                                  const updated = [
                                    ...(field.value || []),
                                    {
                                      label: permission.label,
                                      value: permission.value,
                                    },
                                  ];
                                  if (
                                    viewPermission &&
                                    !updated.some(
                                      (item) =>
                                        item.value === viewPermission.value,
                                    )
                                  ) {
                                    updated.push(viewPermission);
                                  }
                                  field.onChange(updated);
                                } else {
                                  if (
                                    permission.label?.toLowerCase() === "view"
                                  )
                                    return;

                                  field.onChange(
                                    field.value.filter(
                                      (item) => item.value !== permission.value,
                                    ),
                                  );
                                }
                              }}
                            />

                            <label
                              className="form-check-label"
                              htmlFor={`permission-${permission.value}`}
                            >
                              {permission.label}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  )}
                />
                {errors.permissions && (
                  <Error>{errors.permissions.message}</Error>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="d-flex justify-content-end w-100">
          <Button
            type="button"
            className="btn btn_main btn_muted px-3 py-2 mx-1"
            onClick={handleModalHide}
            tabIndex={101}
          >
            Close
          </Button>
          <Button
            type="submit"
            className="btn btn_main px-3 py-2 mx-1"
            tabIndex={100}
            isDisabled={isSubmitting}
          >
            {isSubmitting ? "Loading.." : isEdit ? "Update" : "Create"}
          </Button>
        </div>
      </fieldset>
    </form>
  );
};

export default FormModal;
